/* Small numeric helpers shared by the DSP modules. Pure functions, safe in Node and the browser. */

export type Samples = Float32Array | Float64Array;

export function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

export function median(arr: ArrayLike<number>): number {
  if (!arr.length) return Number.NaN;
  const a = Array.from(arr).sort((x, y) => x - y);
  const m = a.length >> 1;
  return a.length % 2 ? (a[m] as number) : 0.5 * ((a[m - 1] as number) + (a[m] as number));
}

export const freqToMidi = (f: number, a4 = 440): number => 69 + 12 * Math.log2(f / a4);
export const midiToFreq = (m: number, a4 = 440): number => a4 * 2 ** ((m - 69) / 12);

export function rms(buf: Samples, from = 0, to: number = buf.length): number {
  let s = 0;
  for (let i = from; i < to; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / Math.max(1, to - from));
}

/** Level of the last `n` samples after a first difference: rises sharply on plucks. */
export function hfLevel(buf: Samples, n: number): number {
  const L = buf.length;
  const from = Math.max(1, L - n);
  let s = 0;
  for (let i = from; i < L; i++) {
    const d = buf[i] - buf[i - 1];
    s += d * d;
  }
  return Math.sqrt(s / Math.max(1, L - from));
}

// Scratch buffers reused between calls so the per-frame analysis does not allocate.
const workCache = new Map<string, Float64Array>();
export function work(key: string, n: number): Float64Array {
  const id = key + n;
  let w = workCache.get(id);
  if (!w) {
    w = new Float64Array(n);
    workCache.set(id, w);
  }
  return w;
}

const hannCache = new Map<number, Float64Array>();
export function hann(n: number): Float64Array {
  let w = hannCache.get(n);
  if (!w) {
    w = new Float64Array(n);
    for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
    hannCache.set(n, w);
  }
  return w;
}
